import useHasAccess from "@hooks/useHasAccess"

import { Avatar, Button } from "@atoms/index"

import AlertBadge from "@molecules/AlertBadge/AlertBadge"
import { AlertBadgeProps } from "@molecules/AlertBadge/AlertBadge.types"
import AlertBanner from "@molecules/AlertBanner/AlertBanner"
import ListItem from "@molecules/ListItem/ListItem"
import ListItemContent from "@molecules/ListItem/ListItemContent/ListItemContent"
import { ListItemStack } from "@molecules/index"

import { CRM_ENDPOINTS } from "@endpoints/crm"

import { PERMISSION_LEVEL } from "@constants/permissionLevel"

import styles from "./CRMConnectionCard.module.scss"
import { CRMConnectionCardProps } from "./CRMConnectionCard.types"

function getConnectionStatus(status: CRMConnectionStatus): {
    status: AlertBadgeProps["status"]
    label: string
    caption: string
} {
    if (status === "error") {
        return { status: "error", label: "Error", caption: "Needs attention" }
    } else if (status === "connected") {
        return { status: "success", label: "Connected", caption: "Connected through APIDeck" }
    } else {
        return { status: "neutral", label: "Not connected", caption: "Connect a CRM to start syncing." }
    }
}

export default function CRMConnectionCard(props: CRMConnectionCardProps) {
    const { connection, isLoading = false } = props

    const { hasPermission } = useHasAccess()

    const status = connection?.status ?? "not_connected"
    const { status: badgeStatus, label, caption } = getConnectionStatus(status)
    const integrationName = connection?.crm_integration_name || "CRM"

    const canEditSettings = hasPermission("settings_edit_permission", PERMISSION_LEVEL.FULL)

    return (
        <ListItemStack>
            <ListItem isLoading={isLoading}>
                <ListItem.AvatarAndContent
                    avatar={
                        <Avatar
                            variant="company"
                            size="xl"
                            src={connection?.crm_integration_icon_url || undefined}
                            fallbackName={integrationName}
                            isLoading={isLoading}
                        />
                    }
                    content={
                        <ListItemContent
                            isLoading={isLoading}
                            leftLabel={
                                <ListItem.Label
                                    label={integrationName}
                                    suffixSlot={
                                        <AlertBadge
                                            status={badgeStatus}
                                            size="sm"
                                            showIcon={true}
                                            isLoading={isLoading}
                                        >
                                            {label}
                                        </AlertBadge>
                                    }
                                />
                            }
                            leftCaption={<ListItem.Caption caption={caption} truncate={1} />}
                        />
                    }
                />
                {canEditSettings && (
                    <ListItem.Actions>
                        {/* A same-tab redirect, not a link: Vault returns to this page to record the connection. */}
                        <Button
                            variant="solid"
                            size="sm"
                            onClick={() => window.location.assign(CRM_ENDPOINTS.VAULT_AUTHORIZE)}
                        >
                            {status === "not_connected" ? "Connect a CRM" : "Manage connection"}
                        </Button>
                    </ListItem.Actions>
                )}
            </ListItem>
            {status === "error" && connection?.detail && (
                <AlertBanner type="card" status="error" message={connection.detail} className={styles.errorBanner} />
            )}
        </ListItemStack>
    )
}
